/**
 * @techstack/ui - Command Center Design Tokens
 *
 * Extended tokens for the Sales Execution Command Center.
 * Includes themed colors (light/dark) from the design handoff.
 *
 * Design source: /Users/ryanmacdiarmid/Downloads/design_handoff_command_center/
 *
 * NOTE: These tokens are specific to the Command Center UI.
 * The base tokens from tokens.ts are still used for structural values.
 */
// =============================================================================
// COMMAND CENTER - LIGHT THEME
// =============================================================================
export const lightTheme = {
    appBg: '#F7F7F5',
    surface: '#FFFFFF',
    surface2: '#FAFAF8',
    elevated: '#FFFFFF',
    border: '#ECEBE7',
    borderStrong: '#DDDBD5',
    text: '#1A1A17',
    text2: '#6A6963',
    text3: '#9B998F',
    accent: '#FA582D', // PANW orange
    onAccent: '#FFFFFF',
    field: '#F2F1ED',
    ok: '#1E865A',
    warn: '#B0791A',
    danger: '#CE3F2A',
    slate: '#5B6472',
    neutral: '#6E6C65',
    bar: '#EAE9E4',
    scroll: '#D7D5CF',
    // Shadows
    cardShadow: '0 1px 2px rgba(20,20,17,.045), 0 1px 3px rgba(20,20,17,.03)',
    popShadow: '0 16px 50px rgba(20,20,17,.16), 0 3px 10px rgba(20,20,17,.07)',
    glow: '0 0 0 1px color-mix(in srgb, var(--accent) 22%, transparent), 0 8px 30px color-mix(in srgb, var(--accent) 14%, transparent)',
};
// =============================================================================
// COMMAND CENTER - DARK THEME
// =============================================================================
export const darkTheme = {
    appBg: '#121210',
    surface: '#1C1C1A',
    surface2: '#181816',
    elevated: '#232321',
    border: '#2A2A27',
    borderStrong: '#393935',
    text: '#F4F3EE',
    text2: '#A4A29A',
    text3: '#6E6C64',
    accent: '#FF6A41', // PANW orange (dark mode)
    onAccent: '#FFFFFF',
    field: '#232321',
    ok: '#41C98B',
    warn: '#E0A94A',
    danger: '#FF6A52',
    slate: '#9AA2AF',
    neutral: '#A4A29A',
    bar: '#2C2C29',
    scroll: '#3A3A36',
    // Shadows
    cardShadow: '0 1px 2px rgba(0,0,0,.4)',
    popShadow: '0 18px 56px rgba(0,0,0,.55)',
    glow: '0 0 0 1px color-mix(in srgb, var(--accent) 36%, transparent), 0 10px 36px color-mix(in srgb, var(--accent) 22%, transparent)',
};
// =============================================================================
// COMMAND CENTER - TYPOGRAPHY
// =============================================================================
export const commandCenterTypography = {
    fontFamily: {
        ui: "'Hanken Grotesk', -apple-system, 'Helvetica Neue', sans-serif",
        mono: "'JetBrains Mono', monospace",
    },
    fontSize: {
        heroNumeral: '25px',
        heroLarge: '33px',
        sectionTitle: '14.5px',
        sectionTitleLarge: '17px',
        body: '12.5px',
        bodyLarge: '14px',
        label: '10.5px',
        labelSmall: '11px',
        tagMetric: '10px',
        tagLarge: '11px',
    },
    fontWeight: {
        normal: 400,
        medium: 500,
        semibold: 600,
        bold: 700,
        extrabold: 800,
    },
    letterSpacing: {
        tight: '-0.02em',
        normal: '-0.01em',
        wide: '0.5px',
        wider: '0.7px',
    },
};
// =============================================================================
// COMMAND CENTER - BORDER RADIUS
// =============================================================================
export const commandCenterRadius = {
    pill: '6px',
    tag: '8px',
    input: '10px',
    cell: '11px',
    card: '13px',
    cardLarge: '14px',
    frame: '16px',
    page: '18px',
    avatar: '50%',
};
// =============================================================================
// COMMAND CENTER - ANIMATION
// =============================================================================
export const commandCenterAnimation = {
    duration: {
        drawer: '.32s',
        hover: '.14s',
        shimmer: '6s',
        pulse: '1.1s',
        spin: '.7s',
        rise: '.25s',
    },
    easing: {
        drawer: 'cubic-bezier(.22,.61,.36,1)',
        standard: 'ease',
        linear: 'linear',
        inOut: 'ease-in-out',
    },
};
// =============================================================================
// COMMAND CENTER - STATUS COLORS (with backgrounds)
// =============================================================================
/**
 * Generates derived status colors using color-mix.
 * These require CSS variables to be set on the element.
 */
export const statusColors = {
    ok: {
        bg: 'color-mix(in srgb, var(--ok) 14%, var(--surface))',
        fg: 'var(--ok)',
    },
    warn: {
        bg: 'color-mix(in srgb, var(--warn) 15%, var(--surface))',
        fg: 'var(--warn)',
    },
    danger: {
        bg: 'color-mix(in srgb, var(--danger) 13%, var(--surface))',
        fg: 'var(--danger)',
    },
    slate: {
        bg: 'color-mix(in srgb, var(--slate) 12%, var(--surface))',
        fg: 'var(--slate)',
    },
    neutral: {
        bg: 'color-mix(in srgb, var(--neutral) 11%, var(--surface))',
        fg: 'var(--neutral)',
    },
    accent: {
        strong: 'color-mix(in srgb, var(--accent) 82%, #000)',
        tint: 'color-mix(in srgb, var(--accent) 13%, var(--surface))',
        tint2: 'color-mix(in srgb, var(--accent) 7%, var(--surface))',
    },
};
// =============================================================================
// COMMAND CENTER - CELL STATUS (for scheduling grid)
// =============================================================================
export const cellStatus = {
    free: {
        bg: 'var(--ok-bg)',
        border: 'var(--ok)',
        glyph: 'check',
    },
    busy: {
        bg: 'var(--danger-bg)',
        border: 'var(--danger)',
        glyph: 'x',
    },
    tentative: {
        bg: 'var(--warn-bg)',
        border: 'var(--warn)',
        glyph: '~',
    },
    unknown: {
        bg: 'var(--field)',
        border: 'var(--border-strong)',
        glyph: '?',
        dashed: true,
    },
};
// =============================================================================
// CSS VARIABLE GENERATOR
// =============================================================================
/**
 * Generates CSS custom property declarations for a theme.
 */
export function generateThemeCSS(theme) {
    return `
    --app-bg: ${theme.appBg};
    --surface: ${theme.surface};
    --surface-2: ${theme.surface2};
    --elevated: ${theme.elevated};
    --border: ${theme.border};
    --border-strong: ${theme.borderStrong};
    --text: ${theme.text};
    --text-2: ${theme.text2};
    --text-3: ${theme.text3};
    --accent: ${theme.accent};
    --on-accent: ${theme.onAccent};
    --field: ${theme.field};
    --ok: ${theme.ok};
    --warn: ${theme.warn};
    --danger: ${theme.danger};
    --slate: ${theme.slate};
    --neutral: ${theme.neutral};
    --bar: ${theme.bar};
    --scroll: ${theme.scroll};
    --card-shadow: ${theme.cardShadow};
    --pop-shadow: ${theme.popShadow};
    --glow: ${theme.glow};
    /* Derived colors */
    --accent-strong: color-mix(in srgb, var(--accent) 82%, #000);
    --accent-tint: color-mix(in srgb, var(--accent) 13%, var(--surface));
    --accent-tint-2: color-mix(in srgb, var(--accent) 7%, var(--surface));
    --ok-bg: color-mix(in srgb, var(--ok) 14%, var(--surface));
    --warn-bg: color-mix(in srgb, var(--warn) 15%, var(--surface));
    --danger-bg: color-mix(in srgb, var(--danger) 13%, var(--surface));
    --slate-bg: color-mix(in srgb, var(--slate) 12%, var(--surface));
    --neutral-bg: color-mix(in srgb, var(--neutral) 11%, var(--surface));
  `;
}
// =============================================================================
// FULL CSS FOR THEMES
// =============================================================================
export const commandCenterCSS = `
/* Command Center - Light Theme */
[data-theme="light"] {
${generateThemeCSS(lightTheme)}
}

/* Command Center - Dark Theme */
[data-theme="dark"] {
${generateThemeCSS(darkTheme)}
}

/* Command Center - Typography */
:root {
  --cc-font-ui: ${commandCenterTypography.fontFamily.ui};
  --cc-font-mono: ${commandCenterTypography.fontFamily.mono};
}

/* Command Center - Animations */
@keyframes cc-shimmer {
  0% { background-position: -220% 0; }
  100% { background-position: 220% 0; }
}

@keyframes cc-pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: .45; transform: scale(.82); }
}

@keyframes cc-spin {
  to { transform: rotate(360deg); }
}

@keyframes cc-rise {
  from { opacity: 0; transform: translateY(7px); }
  to { opacity: 1; transform: translateY(0); }
}

/* Command Center - Scrollbar */
.cc-scroll::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}
.cc-scroll::-webkit-scrollbar-thumb {
  background: var(--scroll);
  border-radius: 6px;
}
.cc-scroll::-webkit-scrollbar-track {
  background: transparent;
}
`;
// =============================================================================
// TOKEN BUNDLE
// =============================================================================
export const commandCenterTokens = {
    light: lightTheme,
    dark: darkTheme,
    typography: commandCenterTypography,
    radius: commandCenterRadius,
    animation: commandCenterAnimation,
    statusColors,
    cellStatus,
    generateThemeCSS,
    css: commandCenterCSS,
};
export default commandCenterTokens;
